import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const emailParam = searchParams.get("email");

  const [status, setStatus] = useState(token ? "verifying" : "pending");
  // "pending" = show "check your email" message
  // "verifying" = calling API with token
  // "success" = email verified
  // "error" = verification failed
  const [errorMsg, setErrorMsg] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        setStatus("success");
      } catch (err) {
        setStatus("error");
        setErrorMsg(
          err?.response?.data?.detail || "Verification failed. The link may be expired."
        );
      }
    })();
  }, [token]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!emailParam) {
      toast.error("No email address available. Please register again.");
      return;
    }
    try {
      await api.post("/auth/resend-verification", { email: emailParam });
      toast.success("Verification email sent! Check your inbox.");
      setResendCooldown(60);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to resend. Try again later.";
      toast.error(detail);
      // If rate limited, extract seconds remaining
      const match = detail.match(/wait (\d+) seconds/);
      if (match) setResendCooldown(parseInt(match[1], 10));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center">
        {/* ---- Verifying state ---- */}
        {status === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800">Verifying your email...</h2>
            <p className="text-gray-500 mt-2">Please wait a moment.</p>
          </>
        )}

        {/* ---- Pending state ---- */}
        {status === "pending" && (
          <>
            <Mail className="w48 h-48 text-blue-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
            <p className="text-gray-600 mb-2">
              We sent a verification link to
            </p>
            {emailParam && (
              <p className="font-semibold text-gray-800 mb-6">{emailParam}</p>
            )}
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-8 rounded-xl w-full transition-colors"
            >
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Resend Verification Email"}
            </button>
            <button
              onClick={() => navigate("/login")}
              className="mt-3 text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Back to Login
            </button>
          </>
        )}

        {/* ---- Success state ---- */}
        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">Email Verified!</h2>
            <p className="text-gray-600 mt-2 mb-6">
              Your account has been verified. You can now log in.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-xl w-full transition-colors"
            >
              Go to Login
            </button>
          </>
        )}

        {/* ---- Error state ---- */}
        {status === "error" && (
          <>
            <XCircle className="w>16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">Verification Failed</h2>
            <p className="text-gray-600 mt-2 mb-6">{errorMsg}</p>
            <button
              onClick={() => navigate("/login")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-xl w-full transition-colors"
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
